// crypto.subtle polyfill — güvensiz bağlamda (HTTP + IP) PKCE code_challenge için SHA-256 digest
// fallback'i. AuthProvider/userManager'dan (ilk signinRedirect'ten) ÖNCE kurulmalı, o yüzden en üstte.
import './auth/cryptoSubtlePolyfill'

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query'
import PrimeLocaleProvider from './app/PrimeLocaleProvider'

import 'primereact/resources/themes/lara-light-cyan/theme.css'
import 'primereact/resources/primereact.min.css'
import 'primeicons/primeicons.css'

// Self-hosted fontlar — harici CDN yok (anayasa Page Standards). Figma tek tip Inter (Poppins yok).
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'
import '@fontsource/inter/700.css'

import './i18n'
import './index.css'
import './theme/design-tokens.css'
import './theme/primereact-overrides.css'
import './components/ui/ui.css'
import { ToastProvider } from './components/ui/ToastProvider'
import ErrorBoundary from './components/ui/ErrorBoundary'
import AuthProvider from './auth/AuthProvider'
import { isStaleClassificationOption, notifyApiError, type NotifyMeta } from './api/errorNotifier'
import { getApiErrorStatus } from './api/apiError'
import { triggerAppFeedback } from './lib/appFeedback/trigger'
import App from './App.tsx'

// Merkezi hata yönetimi (019): tüm veri erişimi RQ üzerinden gittiğinden, hata → toast eşlemesi
// tek noktada (global onError) yapılır; ekranlar kendi tekrarlı eşlemesini tutmaz. meta ile ekran
// opt-out edebilir (suppressGlobalError / handledCodes).
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) =>
      notifyApiError(error, { meta: query.meta as NotifyMeta | undefined }),
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      notifyApiError(error, { meta: mutation.meta as NotifyMeta | undefined })
      // 127 — seçilen Stratejik Hedef / Katkı bu arada pasife alınmışsa form seçenekleri bayattır
      // (5 dk önbellek): hangi kayıt yolundan gelirse gelsin yeniden çekilir ki kullanıcı düzeltebilsin.
      if (isStaleClassificationOption(error)) {
        queryClient.invalidateQueries({ queryKey: ['action-levels'] })
      }
    },
    // THY AppFeedback (096): deneyim anketinin TEK tetikleme noktası. Hangi mutation'ın tetikleyici
    // olduğu `meta.appFeedback` ile bildirimsel olarak işaretlenir (bkz. lib/appFeedback/trigger.ts).
    // Buradan tetiklemek iki kuralı yapının kendisinden alır: yalnız başarıda çalışır (FR-001) ve
    // bir mutation = bir çağrı, dolayısıyla işlem başına en fazla bir anket (FR-017).
    // Söz beklenmez: anket ikincildir; kullanıcının asıl işi bu noktada zaten bitmiştir.
    onSuccess: (_data, variables, _ctx, mutation) => {
      triggerAppFeedback(mutation.meta, variables)
    },
  }),
  defaultOptions: {
    queries: {
      // 4xx (401/403/404/400 vb.) yeniden denenmez — anlamsız tekrar + 401 retry "fırtınası"nı önler
      // (tekrarlayan istekler oturum-sonu toast'ını/yeniden-kimlik-doğrulama kancasını çoğaltmasın; FR-008).
      retry: (failureCount, error) => {
        const status = getApiErrorStatus(error)
        if (status !== undefined && status >= 400 && status < 500) return false
        return failureCount < 3
      },
    },
  },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/* En dış güvenlik ağı: kabuğun kendisi çökerse yine güvenli bir taban hata ekranı gösterilir. */}
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <PrimeLocaleProvider>
          <ToastProvider>
            <AuthProvider>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </AuthProvider>
          </ToastProvider>
        </PrimeLocaleProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
)
